import React, { useMemo } from 'react'
import { OptimizationLogEntry } from '@/api'
import { lastOptimizeMethodAtom } from '@/atoms/optimization'
import { useAtomValue } from 'jotai'
import { Badge } from 'primereact/badge'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts'

interface OptimizationOverviewPanelProps {
  logs: OptimizationLogEntry[]
}

// ログメッセージから日別稼働データを抽出
// パターン: "[V4] YYYY-MM-DD: produced=N units, skus=M, remaining_items=R, remaining_skus=S[, line_remaining=A=X.XXh B=Y.YYh]"
const RE_PRODUCED_V4 = /\[V4\]\s+(\d{4}-\d{2}-\d{2}):\s+produced=(\d+)\s+units,\s+skus=(\d+),\s+remaining_items=(\d+)(?:.*,\s*line_remaining=([^,\n]+))?/
const RE_PRODUCED_V1 = /^(\d{4}-\d{2}-\d{2}):\s+produced=(\d+)\s+units,\s+skus=(\d+),\s+remaining_items=(\d+)(?:.*,\s*line_remaining=([^,\n]+))?/
const RE_LINE_A = /forcing line A|prioritizing line A/
const RE_LINE_B = /forcing line B|prioritizing line B/
// "A=3.25h B=0.00h" などをパース
const parseLineRemaining = (raw: string): Record<string, number> => {
  const result: Record<string, number> = {}
  for (const m of raw.matchAll(/(\w+)=([\d.]+)h/g)) {
    result[m[1]] = parseFloat(m[2])
  }
  return result
}

type DailyStats = { date: string; produced: number; skus: number; remaining: number; lineRemaining: Record<string, number> }

const OptimizationOverviewPanel: React.FC<OptimizationOverviewPanelProps> = ({ logs }) => {
  const lastOptimizeMethod = useAtomValue(lastOptimizeMethodAtom)
  const isV4 = useMemo(() => logs.some(l => l.message.includes('[V4]')), [logs])

  const algorithmLabel = useMemo(() => {
    if (lastOptimizeMethod) {
      if (lastOptimizeMethod === 'optimize_category_with_user_plan') return 'optimizer_v2 (V4)'
      if (lastOptimizeMethod === 'optimize') return 'optimizer (V1)'
      if (lastOptimizeMethod === 'optimize_simple') return 'optimizer_simple'
      if (lastOptimizeMethod === 'optimize_category') return 'optimizer_category'
      if (lastOptimizeMethod === 'simulation_only') return 'シミュレーションのみ'
      return lastOptimizeMethod
    }
    return isV4 ? 'optimizer_v2 (V4)' : 'optimizer (V1)'
  }, [lastOptimizeMethod, isV4])

  const levelCounts = useMemo(() => {
    const counts = { error: 0, warning: 0, info: 0, debug: 0 } as Record<string, number>
    for (const l of logs) counts[l.level] = (counts[l.level] ?? 0) + 1
    return counts
  }, [logs])

  const dailyStats = useMemo<DailyStats[]>(() => {
    const map = new Map<string, DailyStats>()
    for (const l of logs) {
      const m = RE_PRODUCED_V4.exec(l.message) ?? RE_PRODUCED_V1.exec(l.message)
      if (m) {
        console.log('Parsed log for date', m[1],m[0], 'produced', m[2], 'skus', m[3], 'remaining', m[4], 'lineRemaining', m[5])
        map.set(m[1], {
          date: m[1],
          produced: parseInt(m[2], 10),
          skus: parseInt(m[3], 10),
          remaining: parseInt(m[4], 10),
          lineRemaining: m[5] ? parseLineRemaining(m[5]) : {},
        })
      }
    }
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date))
  }, [logs])

  const lineKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const s of dailyStats) Object.keys(s.lineRemaining).forEach(k => keys.add(k))
    return Array.from(keys).sort()
  }, [dailyStats])

  const lineStats = useMemo(() => {
    let lineA = 0, lineB = 0
    for (const l of logs) {
      if (RE_LINE_A.test(l.message)) lineA++
      if (RE_LINE_B.test(l.message)) lineB++
    }
    return { lineA, lineB }
  }, [logs])

  const LEVEL_SEVERITY: Record<string, 'danger' | 'warning' | 'info' | 'secondary'> = {
    error: 'danger', warning: 'warning', info: 'info', debug: 'secondary',
  }

  return (
    <div className="flex flex-column gap-3 text-sm">
      {/* アルゴリズム & ログ統計 */}
      <div className="flex flex-row flex-wrap gap-3 align-items-center">
        <div className="flex align-items-center gap-2">
          <span className="text-600 font-medium">アルゴリズム:</span>
          <Badge value={algorithmLabel} severity="info" />
        </div>
        <div className="flex align-items-center gap-2">
          {(['error', 'warning', 'info', 'debug'] as const).map(lv => (
            levelCounts[lv] > 0 && (
              <Badge key={lv} value={`${lv}: ${levelCounts[lv]}`} severity={LEVEL_SEVERITY[lv]} />
            )
          ))}
        </div>
      </div>

      {/* ラインローテーション */}
      {(lineStats.lineA > 0 || lineStats.lineB > 0) && (
        <div>
          <p className="m-0 mb-2 font-medium text-600">ライン選択回数</p>
          <div style={{ height: 80 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={[{ name: 'ライン', A: lineStats.lineA, B: lineStats.lineB }]}
                margin={{ top: 0, right: 16, bottom: 0, left: 8 }}
              >
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={40} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="A" name="Aライン" fill="#3B82F6" />
                <Bar dataKey="B" name="Bライン" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* 日別稼働テーブル */}
      {dailyStats.length > 0 && (
        <div>
          <p className="m-0 mb-2 font-medium text-600">日別稼働状況</p>
          <DataTable
            value={dailyStats}
            scrollable
            scrollHeight="200px"
            virtualScrollerOptions={{ itemSize: 36 }}
            size="small"
            className="text-sm"
          >
            <Column field="date" header="日付" style={{ width: '8rem' }} />
            <Column field="produced" header="生産数" style={{ width: '6rem' }} bodyClassName="text-right" />
            <Column field="skus" header="SKU数" style={{ width: '6rem' }} bodyClassName="text-right" />
            <Column field="remaining" header="残余キャパ(個)" style={{ width: '8rem' }} bodyClassName="text-right"
              body={(row: DailyStats) => (
                <span className={row.remaining <= 0 ? 'text-red-500 font-bold' : ''}>{row.remaining}</span>
              )}
            />
            {lineKeys.map(k => (
              <Column key={k} header={`${k}残(h)`} style={{ width: '6rem' }} bodyClassName="text-right"
                body={(row: DailyStats) => {
                  const v = row.lineRemaining[k]
                  if (v === undefined) return <span className="text-400">-</span>
                  return <span className={v <= 0 ? 'text-red-500 font-bold' : ''}>{v.toFixed(2)}</span>
                }}
              />
            ))}
          </DataTable>
        </div>
      )}

      {logs.length === 0 && (
        <p className="text-600 m-0">最適化ログがありません。最適化を実行してください。</p>
      )}
    </div>
  )
}

export default OptimizationOverviewPanel
